import { useState } from "react";
import { ArrowLeft, MessageCircle, PencilLine, ShieldCheck, User } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

const input = "w-full min-w-0 rounded-lg border border-line bg-ink px-3 py-2 text-sm text-chalk outline-none focus:border-lime";
const lbl = "mb-1 block font-mono text-[11px] uppercase tracking-[0.15em] text-dim";

const Choice = ({ icon: Icon, title, text, onClick, testid, tone = "lime" }) => (
  <button type="button" data-testid={testid} onClick={onClick}
    className={`group flex w-full items-start gap-3 rounded-2xl border border-line bg-ink/60 p-4 text-left transition-colors duration-200 ${tone === "wa" ? "hover:border-[#25D366]" : "hover:border-lime"}`}>
    <Icon className={`mt-0.5 h-6 w-6 shrink-0 ${tone === "wa" ? "text-[#25D366]" : "text-lime"}`} />
    <span><span className="block font-display text-lg uppercase text-chalk">{title}</span><span className="block text-sm text-dim">{text}</span></span>
  </button>
);

const Invite = ({ onSend, onBack }) => {
  const [f, setF] = useState({ nom: "", prenom: "", telephone: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const submit = (e) => {
    e.preventDefault();
    setBusy(true);
    onSend({ nom: f.nom.trim(), prenom: f.prenom.trim(), telephone: f.telephone.trim() }).finally(() => setBusy(false));
  };
  return (
    <form onSubmit={submit} className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        <label><span className={lbl}>Nom de l'enfant *</span><input data-testid="add-invite-nom" required maxLength={60} value={f.nom} onChange={set("nom")} className={input} /></label>
        <label><span className={lbl}>Prénom *</span><input data-testid="add-invite-prenom" required maxLength={60} value={f.prenom} onChange={set("prenom")} className={input} /></label>
      </div>
      <label className="block"><span className={lbl}>Téléphone du parent (facultatif)</span>
        <input data-testid="add-invite-tel" type="tel" maxLength={30} value={f.telephone} onChange={set("telephone")} placeholder="Ex. : 0470 12 34 56" className={input} />
      </label>
      <p className="text-xs text-dim">L'enfant est ajouté à l'effectif, puis WhatsApp s'ouvre avec un lien personnel vers la fiche (nom et prénom déjà remplis). Sans numéro, vous choisissez le contact dans WhatsApp. Dès que le parent envoie la fiche, ses infos sont recopiées ici.</p>
      <div className="flex justify-between gap-2">
        <button type="button" data-testid="add-back" onClick={onBack} className="inline-flex items-center gap-1 rounded-full border border-line px-4 py-2 text-sm text-chalk"><ArrowLeft className="h-4 w-4" />Retour</button>
        <button data-testid="add-invite-submit" disabled={busy} className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-5 py-2 text-sm font-semibold text-ink disabled:opacity-60"><MessageCircle className="h-4 w-4" />Ajouter et envoyer</button>
      </div>
    </form>
  );
};

const TITRES = { type: "Ajouter à l'effectif", joueur: "Ajouter un joueur", invite: "Envoyer la fiche au parent" };

export const AddPersonDialog = ({ onClose, onStaff, onManual, onInvite }) => {
  const [step, setStep] = useState("type");
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="add-person-dialog" className="max-h-[90vh] w-[calc(100%-1.5rem)] max-w-md overflow-y-auto rounded-[24px] border-line bg-pitch text-chalk">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl uppercase text-chalk">{TITRES[step]}</DialogTitle>
          <DialogDescription className="text-dim">{step === "type" ? "Qui voulez-vous ajouter ?" : step === "joueur" ? "Comment remplir ses informations ?" : "Le parent remplira lui-même la fiche de renseignements."}</DialogDescription>
        </DialogHeader>
        {step === "type" && (
          <div className="space-y-2">
            <Choice testid="add-type-joueur" icon={User} title="Un joueur" text="Un enfant de l'équipe." onClick={() => setStep("joueur")} />
            <Choice testid="add-type-encadrant" icon={ShieldCheck} title="Un encadrant" text="Coach, délégué, accompagnateur…" onClick={onStaff} />
          </div>
        )}
        {step === "joueur" && (
          <div className="space-y-2">
            <Choice testid="add-mode-manuel" icon={PencilLine} title="Je remplis moi-même" text="Saisir toutes les infos maintenant." onClick={onManual} />
            <Choice testid="add-mode-whatsapp" icon={MessageCircle} tone="wa" title="J'envoie la fiche au parent" text="Par WhatsApp : le parent remplit la fiche de renseignements." onClick={() => setStep("invite")} />
            <button type="button" data-testid="add-back" onClick={() => setStep("type")} className="inline-flex items-center gap-1 rounded-full border border-line px-4 py-2 text-sm text-chalk"><ArrowLeft className="h-4 w-4" />Retour</button>
          </div>
        )}
        {step === "invite" && <Invite onSend={onInvite} onBack={() => setStep("joueur")} />}
      </DialogContent>
    </Dialog>
  );
};
